import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestCreateMember,
  requestViewAllMembers,
  requestUpdateMember,
  requestDeleteMember,
  requestDownloadMemberExcel,
  requestViewTribeMembers,
  requestViewMember,
  requestViewMemberProfile
} from './member.actions';
import {
  getCreateMemberRequest,
  getViewAllMembersRequest,
  getUpdateMemberRequest,
  getDeleteMemberRequest,
  getDownloadMemberExcelRequest,
  getViewTribeMembersRequest,
  getViewMemberRequest,
  getViewMemberProfileRequest
} from './member.endpoints';
import {
  CREATING_MEMBER,
  RETRIEVING_MEMBERS,
  UPDATING_MEMBER,
  DELETING_MEMBER,
  DOWNLOADING_MEMBER_EXCEL,
  RETRIEVING_TRIBE_MEMBERS,
  RETRIEVING_MEMBER,
  RETRIEVING_MEMBER_PROFILE
} from './member.constants';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setMember, setMembers } from '../../slices/programme/member/member.slice';
import { setProfile } from '../../slices/programme/tribe/tribe.slice';

import { SUCCESS, ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

export function* performCreateMemberRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_MEMBER));

    const [endpoint, options] = getCreateMemberRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner(CREATING_MEMBER));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllMembers({ externalReference: input.programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(CREATING_MEMBER));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreateMemberRequest() {
  yield takeLatest(requestCreateMember.type, performCreateMemberRequest);
}

export function* performViewAllMembersRequest({ payload }) {
  const { externalReference, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_MEMBERS));

    const [endpoint, options] = getViewAllMembersRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setMembers(data.members));
    yield put(removeSpinner(RETRIEVING_MEMBERS));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(RETRIEVING_MEMBERS));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewAllMembersRequest() {
  yield takeLatest(requestViewAllMembers.type, performViewAllMembersRequest);
}

export function* performUpdateMemberRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_MEMBER));

    const [endpoint, options] = getUpdateMemberRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner(UPDATING_MEMBER));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllMembers({ externalReference: input.programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(UPDATING_MEMBER));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdateMemberRequest() {
  yield takeLatest(requestUpdateMember.type, performUpdateMemberRequest);
}

export function* performDeleteMemberRequest({ payload }) {
  const { externalReference, programmeExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_MEMBER));

    const [endpoint, options] = getDeleteMemberRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner(DELETING_MEMBER));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllMembers({ externalReference: programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(DELETING_MEMBER));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForDeleteMemberRequest() {
  yield takeLatest(requestDeleteMember.type, performDeleteMemberRequest);
}

export function* performDownloadMemberExcelRequest({ payload }) {
  const { programmeExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner(DOWNLOADING_MEMBER_EXCEL));

    const [endpoint, options] = getDownloadMemberExcelRequest(programmeExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data);
    }

    yield put(removeSpinner(DOWNLOADING_MEMBER_EXCEL));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(DOWNLOADING_MEMBER_EXCEL));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForDownloadMemberExcelRequest() {
  yield takeLatest(requestDownloadMemberExcel.type, performDownloadMemberExcelRequest);
}

export function* performViewTribeMembersRequest({ payload }) {
  const { externalReference, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_TRIBE_MEMBERS));

    const [endpoint, options] = getViewTribeMembersRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setMembers(data.members));
    yield put(removeSpinner(RETRIEVING_TRIBE_MEMBERS));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(RETRIEVING_TRIBE_MEMBERS));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewTribeMembersRequest() {
  yield takeLatest(requestViewTribeMembers.type, performViewTribeMembersRequest);
}

export function* performViewMemberRequest({ payload }) {
  const { externalReference } = payload;

  try {
    yield put(addSpinner(RETRIEVING_MEMBER));

    const request = getViewMemberRequest(externalReference);

    const { data } = yield call(axios, ...request);

    yield put(setMember(data.members[0]));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_MEMBER));
  }
}

export function* watchForViewMemberRequest() {
  yield takeLatest(requestViewMember.type, performViewMemberRequest);
}

export function* performViewMemberProfileRequest({ payload }) {
  const { memberExternalReference } = payload;

  try {
    yield put(addSpinner(RETRIEVING_MEMBER_PROFILE));

    const request = getViewMemberProfileRequest(memberExternalReference);

    const { data } = yield call(axios, ...request);

    delete data.message;
    yield put(setProfile(data));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_MEMBER_PROFILE));
  }
}

export function* watchForViewMemberProfileRequest() {
  yield takeLatest(requestViewMemberProfile.type, performViewMemberProfileRequest);
}

export default function* memberSaga() {
  yield all([
    watchForCreateMemberRequest(),
    watchForViewAllMembersRequest(),
    watchForUpdateMemberRequest(),
    watchForDeleteMemberRequest(),
    watchForDownloadMemberExcelRequest(),
    watchForViewTribeMembersRequest(),
    watchForViewMemberRequest(),
    watchForViewMemberProfileRequest()
  ]);
}
